/**
 * Place-offer and counter-offer — what the buyer posts to
 * `POST /api/offers` and `POST /api/offers/[id]/counter`.
 *
 * Both bodies are validated additively at the route boundary: every field
 * the route already reads is parsed, with the same numeric ranges the route
 * enforced inline pre-2026-06-09 (`amount` 0 < x <= 999_999, `expires_in_hours`
 * 1..168, `message` max 500). The DB-state-dependent checks (listing exists,
 * seller is online, prior-offer dedup) stay inline because they need the row.
 *
 * Why additive-only (Sibling-A P1, mobile-consumed): mobile posts both
 * shapes verbatim from `apps/mobile/lib/api-offers.ts` (`placeOffer`,
 * `counterOffer`). Until mobile-lock we can ONLY add fields / loosen — never
 * tighten existing constraints — without forcing a coordinated app release.
 *
 * @see app/api/offers/route.ts — place-offer handler
 * @see app/api/offers/[id]/counter/route.ts — counter-offer handler
 * @see apps/mobile/lib/api-offers.ts — mobile callers
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md C-7, C-8
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Shared limits — kept in one place so the two endpoints stay aligned and the
 * server-side route handlers reuse the same caps.
 */
export const OFFER_AMOUNT_MAX = 999_999;
export const OFFER_MESSAGE_MAX_LENGTH = 500;
export const OFFER_EXPIRES_HOURS_MIN = 1;
export const OFFER_EXPIRES_HOURS_MAX = 168; // 7 days

// ---------------------------------------------------------------------------
// Place-offer
// ---------------------------------------------------------------------------

export interface PlaceOfferRequest {
  /** Target listing's `listings.id`. */
  listing_id: string;
  /** Offer amount in listing currency. 0 < amount <= 999_999. */
  amount: number;
  /** Optional buyer note. Max 500 chars. */
  message?: string | null;
  /**
   * Optional `shipping_methods.id` selection. Server coerces missing to null.
   * Sent by NO client today (web's offer-modal passes it as `null`, mobile
   * omits the key — verified 2026-08-02) and the `place_offer` RPC branch
   * reading it is a no-op: shipping is resolved fresh from the listing at
   * settlement checkout, not at offer time. Kept for forward compatibility
   * only — wiring it up is a cross-repo feature (picker UI + RPC amount
   * computation + settlement seeding), not a one-side fix.
   */
  shipping_method_id?: string | null;
  /** Hours until the offer expires. Integer in [1, 168]. */
  expires_in_hours: number;
  /**
   * Optional client-supplied idempotency token. Stored on the offer row so a
   * mobile retry of the same logical action coalesces server-side.
   */
  client_offer_id?: string;
}

export const PlaceOfferRequestSchema = pinned<PlaceOfferRequest>()(
  z.object({
    listing_id: z.string().min(1),
    amount: z.number().finite().positive().max(OFFER_AMOUNT_MAX),
    // `.nullish()` not `.optional()` — `hooks/use-offer.ts:108` sends
    // `message: input.message ?? null`, so the wire form is always the key,
    // either with a string or literal `null`. `.optional()` rejects `null`.
    message: z.string().max(OFFER_MESSAGE_MAX_LENGTH).nullish(),
    shipping_method_id: z.string().min(1).nullable().optional(),
    expires_in_hours: z
      .number()
      .int()
      .min(OFFER_EXPIRES_HOURS_MIN)
      .max(OFFER_EXPIRES_HOURS_MAX),
    client_offer_id: z.string().min(1).optional(),
  }),
);

// ---------------------------------------------------------------------------
// Counter-offer
// ---------------------------------------------------------------------------

export interface CounterOfferRequest {
  /** Counter amount in listing currency. 0 < amount <= 999_999. */
  amount: number;
  /** Optional counter note. Max 500 chars. */
  message?: string | null;
  /** Hours until the counter expires. Integer in [1, 168]. */
  expires_in_hours: number;
  /** Optional client-supplied idempotency token (same role as place-offer). */
  client_offer_id?: string;
}

export const CounterOfferRequestSchema = pinned<CounterOfferRequest>()(
  z.object({
    amount: z.number().finite().positive().max(OFFER_AMOUNT_MAX),
    // Same null-wire-form as place-offer — see PlaceOfferRequestSchema comment.
    message: z.string().max(OFFER_MESSAGE_MAX_LENGTH).nullish(),
    expires_in_hours: z
      .number()
      .int()
      .min(OFFER_EXPIRES_HOURS_MIN)
      .max(OFFER_EXPIRES_HOURS_MAX),
    client_offer_id: z.string().min(1).optional(),
  }),
);

// ---------------------------------------------------------------------------
// List-offers filter (GET /api/offers)
// ---------------------------------------------------------------------------

/**
 * Optional `listing_id` query param on `GET /api/offers` — server-side filter
 * to "offers for this listing" (added 2026-07-13, mobile listing-perf: the
 * app was paging the full list client-side to find a pending sent offer for
 * one listing). Must be a uuid (`listings.id`); the route rejects a present
 * but malformed value with 400 VALIDATION_ERROR. Absent param = unfiltered
 * list (backward compatible).
 *
 * @see app/api/offers/route.ts — GET handler
 * @see lib/offers/offer-fetch.ts — fetchOffersForUser `listingId` option
 */
export const OffersListingIdFilterSchema = pinned<string>()(z.string().uuid());
